import { useTranslations } from "next-intl";
import { PBanner } from "./PBanner";
import { useExpiringSubscription } from "@/hooks/useExpiringSubscription";
import { useCompanyRoleString } from "@/hooks/useCompanyRoleString";
import { useTenantBlocked } from "@/contexts/TenantBlockedContext";
import { dismissAtRung, useRungDismissed } from "@/lib/storage/dismissRung";
import { RUNG_DISMISSIBLE, RUNG_ORDINAL, RUNG_TONE } from "@/lib/ui/expiryTone";

/**
 * Global dashboard strip warning that the company subscription is about to
 * end — a scheduled cancellation approaching ("ends in X days") or a failed
 * renewal payment (past-due). Only billing-capable roles (OWNER/ADMIN) see it,
 * and it yields to the blocking tenant-restricted banner so the two never
 * stack.
 *
 * The tone escalates with the remaining days (informative → warning → red),
 * and the dismissal is stored as the rung it silenced: waving away "ends in 12
 * days" does not silence "ends in 2 days" or the day the payment fails. The
 * last two rungs cannot be dismissed at all.
 */
export const SubscriptionExpiryBannerHost: React.FC = () => {
  const { show, reason, rung, daysRemaining, plan, subscriptionId } = useExpiringSubscription();
  const role = useCompanyRoleString();
  const { blocked } = useTenantBlocked();
  const t = useTranslations("userSettings.subscriptionExpiry");
  const tUser = useTranslations("userSettings");

  // One key per subscription — the day and the rung live in the value. The
  // previous shape baked both into the key, minting a new localStorage entry
  // every single day and never sweeping any of them.
  const dismissKey = `subExpiryDismiss:${subscriptionId ?? "?"}`;
  const ordinal = RUNG_ORDINAL[rung];
  // Hook call stays above every early return.
  const dismissed = useRungDismissed(dismissKey, ordinal);

  if (!show) return null;
  if (role !== "OWNER" && role !== "ADMIN") return null;
  // Don't stack on top of the blocking (red) tenant-restricted banner.
  if (blocked) return null;
  if (dismissed && RUNG_DISMISSIBLE[rung]) return null;

  const pastDue = reason === "pastDue";
  const planLabel = plan ? tUser(plan) : "";

  return (
    <PBanner
      testId="subscription-expiry-banner"
      rung={rung}
      tone={RUNG_TONE[rung]}
      shape="strip"
      title={pastDue ? t("pastDueTitle") : t("cancelTitle", { days: daysRemaining })}
      description={
        pastDue
          ? t("pastDueDescription", { plan: planLabel })
          : t("cancelDescription", { days: daysRemaining, plan: planLabel })
      }
      onDismiss={
        RUNG_DISMISSIBLE[rung] ? () => dismissAtRung(dismissKey, ordinal) : undefined
      }
      actionHref="/user/subscription/manage"
      actionLabel={t("manageAction")}
    />
  );
};
